import {
  Button,
  OutlinedSelect,
  SelectOptionType,
  TextOutlinedInput,
  useMobile,
} from '@octano/global-ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { useStudentListLoader } from './StudentListLoader';

const keyPrefix = 'financialPortal.currentAccount.list.filters';

interface FormShape {
  identificationType: SelectOptionType | null;
  identification: string;
  name: string;
  school: SelectOptionType | null;
  studyPlan: SelectOptionType | null;
  financialStatus: SelectOptionType | null;
  overdueDays: SelectOptionType | null;
}

const EMPTY_FORM: FormShape = {
  identificationType: null,
  identification: '',
  name: '',
  school: null,
  studyPlan: null,
  financialStatus: null,
  overdueDays: null,
};

const FINANCIAL_STATUS_KEYS = [
  { key: 'statusActive', value: 'active' },
  { key: 'statusFinancialBlock', value: 'financialBlock' },
  { key: 'statusTemporaryUnblock', value: 'temporaryUnblock' },
] as const;

const OVERDUE_DAYS_OPTIONS: number[] = [5, 15, 30, 60, 90];

export interface StudentListFiltersProps {
  identificationTypes: SelectOptionType[];
  schools: SelectOptionType[];
  studyPlans: SelectOptionType[];
}

export default function StudentListFilters({
  identificationTypes,
  schools,
  studyPlans,
}: StudentListFiltersProps) {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { updateQuery } = useStudentListLoader();

  const { control, handleSubmit, reset } = useForm<FormShape>({
    defaultValues: EMPTY_FORM,
  });

  const financialStatusOptions: SelectOptionType[] = FINANCIAL_STATUS_KEYS.map(
    ({ key, value }) => ({
      label: t(`${keyPrefix}.${key}`),
      value,
    }),
  );

  const overdueDaysOptions: SelectOptionType[] = OVERDUE_DAYS_OPTIONS.map(
    (days) => ({
      label: t(`${keyPrefix}.overdueDaysOption`, { days }),
      value: days,
    }),
  );

  const onSubmit = (values: FormShape) => {
    updateQuery({
      page: 0,
      items_per_page: 10,
      identificationTypeId:
        (values.identificationType?.value as number) ?? undefined,
      identification: values.identification || undefined,
      name: values.name || undefined,
      schoolId: (values.school?.value as number) ?? undefined,
      studyPlanId: (values.studyPlan?.value as number) ?? undefined,
      financialStatus: (values.financialStatus?.value as string) ?? undefined,
      overdueDays: (values.overdueDays?.value as number) ?? undefined,
    });
  };

  const onClear = () => {
    reset(EMPTY_FORM);
    updateQuery({ page: 0, items_per_page: 10 });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row className="pb-4 gutters-filters">
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="identificationType"
            control={control}
            label={t(`${keyPrefix}.identificationType`)}
            placeholder={t(`${keyPrefix}.placeholderIdentificationType`)}
            options={identificationTypes}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <TextOutlinedInput
            name="identification"
            control={control}
            label={t(`${keyPrefix}.identification`)}
            placeholder={t(`${keyPrefix}.placeholderIdentification`)}
          />
        </Col>
        <Col xs={12} md={3}>
          <TextOutlinedInput
            name="name"
            control={control}
            label={t(`${keyPrefix}.name`)}
            placeholder={t(`${keyPrefix}.placeholderName`)}
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="school"
            control={control}
            label={t(`${keyPrefix}.school`)}
            placeholder={t(`${keyPrefix}.placeholderSchool`)}
            options={schools}
            isClearable
          />
        </Col>
      </Row>
      <Row className="pb-4 gutters-filters">
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="studyPlan"
            control={control}
            label={t(`${keyPrefix}.studyPlan`)}
            placeholder={t(`${keyPrefix}.placeholderStudyPlan`)}
            options={studyPlans}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="financialStatus"
            control={control}
            label={t(`${keyPrefix}.financialStatus`)}
            placeholder={t(`${keyPrefix}.placeholderFinancialStatus`)}
            options={financialStatusOptions}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="overdueDays"
            control={control}
            label={t(`${keyPrefix}.overdueDays`)}
            placeholder={t(`${keyPrefix}.placeholderOverdueDays`)}
            options={overdueDaysOptions}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <div className="d-flex justify-content-end">
            <Button
              type="submit"
              text={t(`${keyPrefix}.search`)}
              size="sm"
              style={{
                width: '100%',
                height: 41,
                marginTop: isMobile ? 0 : '1.5rem',
              }}
            />
            <Button
              text={t(`${keyPrefix}.clear`)}
              size="sm"
              outlined
              onClick={onClear}
              style={{
                width: '100%',
                height: 41,
                marginTop: isMobile ? '1rem' : '1.5rem',
                marginLeft: 10,
              }}
            />
          </div>
        </Col>
      </Row>
    </Form>
  );
}
